import { Injectable, OnDestroy, inject } from '@angular/core';
import { Regions } from '@app/core/services/api/API.service';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from '../../util.service';
import { take } from 'rxjs/operators';
import * as subscriptions from '@app/core/graphql/subscriptions';
import {
    AmplifyApiService,
    RegionsService as RegionsApiService
} from '@app/core/api/services';

@Injectable({
    providedIn: 'root'
})
export class RegionsService implements OnDestroy {
    private _regions$ = new BehaviorSubject<Regions[]>([]);
    private _loading$ = new BehaviorSubject<boolean>(true);
    private regions: Regions[] = [];
    private subscriptions: any[] = [];
    private _regionsApiService = inject(RegionsApiService);
    private _amplifyApiService = inject(AmplifyApiService);

    constructor(private _utilService: UtilService) {
        this.subscriptions.push(
            this._amplifyApiService
                .subscribe({ query: subscriptions.onCreateRegions })
                .subscribe(observer => {
                    const data = observer.data.onCreateRegions;
                    this.regions.push(data);
                    this.regions = this._utilService.sortJSON(
                        this.regions,
                        'name',
                        'asc'
                    );
                    this._regions$.next(this.regions);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onUpdateRegions })
                .subscribe(observer => {
                    const data = observer.data.onUpdateRegions;
                    this.regions = this.regions.map((region: Regions) => {
                        return region.id === data.id ? data : region;
                    });
                    this.regions = this._utilService.sortJSON(
                        this.regions,
                        'name',
                        'asc'
                    );
                    this._regions$.next(this.regions);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onDeleteRegions })
                .subscribe(observer => {
                    const data = observer.data.onDeleteRegions;
                    this.regions = this.regions.filter((region: Regions) => {
                        return region.id !== data.id;
                    });
                    this._regions$.next(this.regions);
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get regions$() {
        return this._regions$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    async listRegions() {
        const cached = this._regions$.getValue();
        if (cached?.length > 0) {
            this._loading$.next(false);
            return;
        }

        try {
            this._loading$.next(true);
            this.regions = [];
            this.regions = await this.listRegionsBD();
        } catch (err) {
            console.log(err);
        } finally {
            this._regions$.next(this.regions);
            this._loading$.next(false);
        }
    }

    async findRegionById(id: string): Promise<Regions | undefined> {
        try {
            const regions = await this.regions$.pipe(take(1)).toPromise();
            return regions.find(region => region.id === id);
        } catch (error) {
            console.error('Error finding region:', error);
            return undefined;
        }
    }

    private async listRegionsBD(): Promise<Regions[]> {
        const items = await this._regionsApiService.listRegions(200);
        return this._utilService.sortJSON(items ?? [], 'name', 'asc');
    }
}
